// frontend/src/services/payment.service.ts
import api from './api';
import axios from 'axios';
import { Contribution, ContributionSummary } from '../types';

interface CreateContributionData {
  amount: number;
  contributorName: string;
  contributorEmail?: string;
  message?: string;
  type?: 'gift' | 'wedding_fund' | 'honeymoon' | 'other';
  couple: string;
}

interface InitializePaymentData {
  amount: number;
  email: string;
  currency?: string;
  metadata: {
    contributionType?: string;  
    contributorName?: string;
    contributorEmail?: string;
    message?: string;
    type?: string;
    couple?: string;
    giftId?: string;  
    registryItem?: string;
    currency?: string;
  };
}

interface PaystackResponse {
  status: boolean;
  message: string;
  data: {
    authorization_url: string;
    access_code: string;
    reference: string;
  };
}
interface VerifyResponse {
  success: boolean;
  message: string;
  needsVerification?: boolean;
  isPremium?: boolean;
  premiumPlan?: string;
  isSubscription?: boolean;
  isContribution?: boolean;
  contribution?: {
    _id: string;
    amount: number;
    contributorName: string;
    contributorEmail?: string;
    currency?: string;
    couple?: string;
  };
  plan?: {
    type: string;
    duration: string;
  };
  email?: string;
}
const PaymentService = {
  /**
   * Create a new contribution record
   */
  createContribution: async (contributionData: CreateContributionData): Promise<Contribution> => {
    const response = await api.post<Contribution>('/payments/contributions', contributionData);
    return response.data;
  },

  /**
   * Get all contributions for a couple
   */
  getCoupleContributions: async (coupleId: string): Promise<Contribution[]> => {
    const response = await api.get<Contribution[]>(`/payments/contributions/couple/${coupleId}`);
    return response.data;
  },

  /**
   * Initialize a Paystack transaction (requires authentication)
   */
  initializePaystackTransaction: async (transactionData: InitializePaymentData): Promise<PaystackResponse> => {
    const response = await api.post<PaystackResponse>('/payments/paystack/initialize', transactionData);
    return response.data;
  },

  /**
   * Initialize a Paystack transaction without authentication
   * Used for guest contributions where the user is not logged in
   */
  publicInitializeTransaction: async (transactionData: InitializePaymentData): Promise<PaystackResponse> => {
    const apiUrl = process.env.REACT_APP_API_URL || 'https://wedding-backend-p3y8.onrender.com';
    const response = await axios.post<PaystackResponse>(
      `${apiUrl}/payments/paystack/public-initialize`, 
      transactionData,
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data;
  },

  /**
   * Verify a Paystack transaction (requires authentication)
   */
  verifyPaystackTransaction: async (reference: string): Promise<VerifyResponse> => {
    const response = await api.get<VerifyResponse>(`/payments/paystack/verify/${reference}`);
    return response.data;
  },

  /**
   * Verify a Paystack transaction without authentication
   * Used for guest contributions verification
   */
  publicVerifyTransaction: async (reference: string): Promise<VerifyResponse> => {
    const apiUrl = process.env.REACT_APP_API_URL || 'https://wedding-backend-p3y8.onrender.com';
    const response = await axios.post<VerifyResponse>(
      `${apiUrl}/payments/paystack/public-verify`,
      { reference },
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data;
  },

  /**
   * Get contribution summary for a couple
   */
  async getContributionSummary(coupleId: string): Promise<ContributionSummary> {
    try {
      const response = await api.get<ContributionSummary>(`/payments/contributions/summary/${coupleId}`);
      console.log(`Contribution summary for couple ${coupleId}:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`Error fetching contribution summary for couple ${coupleId}:`, error);
      throw error;
    }
  }
};

export default PaymentService;